import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Modal, Form, Select, Space, Tag, Popconfirm, message, Input, Tabs } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { addStudentParent, updateStudentParent } from '../../api/students';
import { createUser } from '../../api/users';
import UserSearchSelect from '../common/UserSearchSelect';
import api from '../../api/axios';
import useAuthStore from '../../store/authStore';
import { toEmailLocalPart } from '../../utils/emailLocalPart';
import PhoneInput from '../common/PhoneInput';
import { apiErrorMessage } from '../../utils/apiError';
import { VeliPortalRozeti } from '../common/VeliPortalRozeti';
import { formatPhone } from '../../utils/phone';

const MANAGER_ROLES = ['developer_admin', 'super_admin', 'branch_manager'];

const { Option } = Select;

const PARENT_EMAIL_DOMAIN = process.env.REACT_APP_PARENT_EMAIL_DOMAIN || null;

const StudentParentsTab = ({ studentId, parents, onUpdate, branchId, readOnly = false }) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const canViewUserDetail = MANAGER_ROLES.includes(user?.role);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('existing');
  const [existingForm] = Form.useForm();
  const [newForm] = Form.useForm();
  const [emailTouched, setEmailTouched] = useState(false);
  const isAutoFillingEmail = useRef(false);
  // Düzenlenen veli bağı (StudentParent kaydı) — null ise modal kapalı.
  const [editing, setEditing] = useState(null);
  const [editForm] = Form.useForm();

  const handleOpen = () => {
    setActiveTab('existing');
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    existingForm.resetFields();
    newForm.resetFields();
    setEmailTouched(false);
  };

  const handleNewFormValuesChange = (changedValues) => {
    if (isAutoFillingEmail.current) return;

    if ('email' in changedValues) {
      setEmailTouched(true);
      return;
    }

    if (!PARENT_EMAIL_DOMAIN || emailTouched) return;

    if ('name' in changedValues || 'surname' in changedValues) {
      const { name = '', surname = '' } = newForm.getFieldsValue(['name', 'surname']);
      isAutoFillingEmail.current = true;
      newForm.setFieldsValue({ email: toEmailLocalPart(`${name}${surname}`) });
      isAutoFillingEmail.current = false;
    }
  };

  const handleAddExisting = async (values) => {
    try {
      await addStudentParent(studentId, values);
      message.success('Veli eklendi.');
      handleClose();
      onUpdate();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Veli eklenemedi.'));
    }
  };

  const handleCreateAndAdd = async (values) => {
    try {
      const email = PARENT_EMAIL_DOMAIN ? `${values.email}@${PARENT_EMAIL_DOMAIN}` : values.email;

      // Yeni veli kullanıcısı oluştur (öğrencinin şubesiyle)
      const userRes = await createUser({ ...values, email, role: 'parent', branch_id: branchId || undefined });
      const newUserId = userRes.data.id;

      // Öğrenciye bağla
      await addStudentParent(studentId, {
        user_id: newUserId,
        relation: values.relation,
        receive_notifications: values.receive_notifications ?? true,
      });

      // Şifre sunucudan geliyor — istemcide sabit yazılı değil.
      Modal.success({
        title: 'Veli Oluşturuldu ve Eklendi',
        content: (
          <div>
            <p>{values.name} {values.surname} için hesap oluşturuldu.</p>
            <p>Şifre: <strong>{userRes.data.default_password}</strong></p>
            <p className="text-gray-400 text-sm">Veli ilk girişte bu şifreyi değiştirmek zorunda kalacak.</p>
          </div>
        ),
      });
      handleClose();
      onUpdate();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Veli oluşturulamadı.'));
    }
  };

  // Veli bağının yakınlık ve bildirim tercihini düzenler. Velinin kendisi
  // değişmiyor — başka bir veli bağlanacaksa bağ silinip yenisi eklenir.
  const handleEditOpen = (p) => setEditing(p);

  // Alanlar effect içinde dolduruluyor, handleEditOpen içinde değil: Modal
  // çocuklarını ilk açılışa kadar mount etmiyor, dolayısıyla satırdaki
  // butona basıldığı an form henüz bağlanmamış olabilir ve setFieldsValue
  // boşa gider. Effect render sonrası çalıştığı için bu zamanlamadan
  // etkilenmiyor.
  useEffect(() => {
    if (!editing) return;
    editForm.setFieldsValue({
      relation: editing.relation,
      receive_notifications: !!editing.receive_notifications,
    });
  }, [editing, editForm]);

  const handleEditClose = () => {
    setEditing(null);
    editForm.resetFields();
  };

  const handleEditSubmit = async (values) => {
    try {
      await updateStudentParent(studentId, editing.id, values);
      message.success('Veli bilgisi güncellendi.');
      handleEditClose();
      onUpdate();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Güncelleme başarısız.'));
    }
  };

  const handleDelete = async (parentId) => {
    try {
      await api.delete(`/students/${studentId}/parents/${parentId}`);
      message.success('Veli bağı kaldırıldı.');
      onUpdate();
    } catch {
      message.error('İşlem başarısız.');
    }
  };

  const relationOptions = (
    <>
      <Option value="mother">Anne</Option>
      <Option value="father">Baba</Option>
      <Option value="guardian">Vasi</Option>
    </>
  );

  return (
    <>
      <Card
        extra={!readOnly && (
          <Button type="primary" icon={<PlusOutlined />} size="small" onClick={handleOpen}>
            Veli Ekle
          </Button>
        )}
      >
        {parents.map((p) => (
          <div key={p.id} className="flex justify-between items-center p-3 border rounded mb-2">
            <div>
              {canViewUserDetail && p.user?.id ? (
                <Button type="link" style={{ padding: 0, height: 'auto', fontWeight: 'bold' }} onClick={() => navigate(`/users/${p.user.id}`)}>
                  {p.user?.name} {p.user?.surname}
                </Button>
              ) : (
                <strong>{p.user?.name} {p.user?.surname}</strong>
              )}
              <span className="ml-2 text-gray-500">
                {p.relation === 'mother' ? 'Anne' : p.relation === 'father' ? 'Baba' : 'Vasi'}
              </span>
              <span className="ml-2 text-gray-400">{formatPhone(p.user?.phone)}</span>
              {/* "Velisi bağlandı mı" — öğretmen/yönetici buradan görüp
                  veliye hatırlatabilsin diye. */}
              <span className="ml-2">
                <VeliPortalRozeti
                  girdi={!!p.user?.last_login_at}
                  aktiflesti={!!p.user?.activated_at}
                  lastLoginAt={p.user?.last_login_at}
                />
              </span>
            </div>
            <Space>
              <Tag color={p.receive_notifications ? 'green' : 'red'}>
                {p.receive_notifications ? 'Bildirim Açık' : 'Bildirim Kapalı'}
              </Tag>
              {/* readOnly kontrolü eskiden yalnızca "Veli Ekle" butonundaydı,
                  silme açıkta kalıyordu. Düzenleme eklenirken ikisi birden
                  bayrağa bağlandı. */}
              {!readOnly && (
                <>
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => handleEditOpen(p)}
                    title="Yakınlık / bildirim tercihini düzenle"
                  />
                  <Popconfirm
                    title="Veli bağını kaldırmak istediğinize emin misiniz?"
                    onConfirm={() => handleDelete(p.id)}
                    okText="Evet"
                    cancelText="Hayır"
                  >
                    <Button icon={<DeleteOutlined />} danger size="small" />
                  </Popconfirm>
                </>
              )}
            </Space>
          </div>
        ))}
        {parents.length === 0 && <p className="text-gray-400">Henüz veli eklenmemiş.</p>}
      </Card>

      <Modal
        title={editing ? `${editing.user?.name} ${editing.user?.surname} — Düzenle` : 'Düzenle'}
        open={!!editing}
        onCancel={handleEditClose}
        footer={null}
        width={420}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
          <Form.Item name="relation" label="Yakınlık" rules={[{ required: true, message: 'Yakınlık seçiniz.' }]}>
            <Select>{relationOptions}</Select>
          </Form.Item>
          <Form.Item name="receive_notifications" label="Bildirim">
            <Select>
              <Option value={true}>Bildirim Açık</Option>
              <Option value={false}>Bildirim Kapalı</Option>
            </Select>
          </Form.Item>
          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={handleEditClose}>İptal</Button>
              <Button type="primary" htmlType="submit">Kaydet</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Veli Ekle" open={modalOpen} onCancel={handleClose} footer={null} width={520}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'existing',
              label: 'Mevcut Veli',
              children: (
                <Form form={existingForm} layout="vertical" onFinish={handleAddExisting}>
                  <Form.Item name="user_id" label="Veli" rules={[{ required: true, message: 'Veli seçiniz.' }]}>
                    <UserSearchSelect role="parent" placeholder="Veli aramak için en az 2 karakter yazın..." />
                  </Form.Item>
                  <Form.Item name="relation" label="Yakınlık" rules={[{ required: true, message: 'Yakınlık seçiniz.' }]}>
                    <Select placeholder="Yakınlık seçiniz">{relationOptions}</Select>
                  </Form.Item>
                  <Form.Item name="receive_notifications" label="Bildirim" initialValue={true}>
                    <Select>
                      <Option value={true}>Bildirim Açık</Option>
                      <Option value={false}>Bildirim Kapalı</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item className="mb-0 flex justify-end">
                    <Space>
                      <Button onClick={handleClose}>İptal</Button>
                      <Button type="primary" htmlType="submit">Ekle</Button>
                    </Space>
                  </Form.Item>
                </Form>
              ),
            },
            {
              key: 'new',
              label: 'Yeni Veli Oluştur',
              children: (
                <Form form={newForm} layout="vertical" onFinish={handleCreateAndAdd} onValuesChange={handleNewFormValuesChange}>
                  <div className="grid grid-cols-2 gap-4">
                    <Form.Item name="name" label="Ad" rules={[{ required: true, message: 'Ad giriniz.' }]}>
                      <Input placeholder="Ad" />
                    </Form.Item>
                    <Form.Item name="surname" label="Soyad" rules={[{ required: true, message: 'Soyad giriniz.' }]}>
                      <Input placeholder="Soyad" />
                    </Form.Item>
                  </div>
                  {PARENT_EMAIL_DOMAIN ? (
                    <Form.Item
                      name="email"
                      label="E-posta"
                      rules={[
                        { required: true, message: 'E-posta giriniz.' },
                        { pattern: /^[^@\s]+$/, message: '@ işareti olmadan sadece kullanıcı adını giriniz.' },
                      ]}
                    >
                      <Input placeholder="adsoyad" addonAfter={`@${PARENT_EMAIL_DOMAIN}`} />
                    </Form.Item>
                  ) : (
                    <Form.Item name="email" label="E-posta" rules={[{ required: true, message: 'E-posta giriniz.' }, { type: 'email', message: 'Geçerli e-posta giriniz.' }]}>
                      <Input placeholder="ornek@email.com" />
                    </Form.Item>
                  )}
                  <Form.Item name="phone" label="Telefon" rules={[{ pattern: /^\+\d{8,15}$/, message: 'Ülke kodunu seçip numarayı giriniz.' }]}>
                    <PhoneInput />
                  </Form.Item>
                  <Form.Item name="relation" label="Yakınlık" rules={[{ required: true, message: 'Yakınlık seçiniz.' }]}>
                    <Select placeholder="Yakınlık seçiniz">{relationOptions}</Select>
                  </Form.Item>
                  <Form.Item name="receive_notifications" label="Bildirim" initialValue={true}>
                    <Select>
                      <Option value={true}>Bildirim Açık</Option>
                      <Option value={false}>Bildirim Kapalı</Option>
                    </Select>
                  </Form.Item>
                  {/* Şifre değeri BİLEREK burada yazmıyor: REACT_APP_* değerleri
                      derlenmiş pakete gömülür, siteyi açan herkes okuyabilir.
                      Değer sunucudan geliyor ve kayıttan sonra gösteriliyor. */}
                  <div className="text-gray-400 text-sm mb-4">
                    Veliye varsayılan bir şifre atanacak ve kayıttan sonra gösterilecek.
                    Veli ilk girişte değiştirmek zorunda kalacak.
                  </div>
                  <Form.Item className="mb-0 flex justify-end">
                    <Space>
                      <Button onClick={handleClose}>İptal</Button>
                      <Button type="primary" htmlType="submit">Oluştur ve Ekle</Button>
                    </Space>
                  </Form.Item>
                </Form>
              ),
            },
          ]}
        />
      </Modal>
    </>
  );
};

export default StudentParentsTab;
